/** Staged images — `POST /media/staged/` (plan §9.4, D3: images upload first).
 *
 *  Picking a file for a catalog record's image field (a category's icon, an itinerary's
 *  route map, a day's image) uploads it at once; the draft then holds the returned
 *  `image_id` and the record's save attaches it. An upload no save attaches within a day
 *  is removed by the daily sweep, and a save that names it is refused with the code
 *  `staged_image_gone` and the server's "… Upload it again." (`isSweptUpload`).
 *
 *  Multipart: a `_json` part (`{purpose, tour_operator}`) and one `file` part. The answer
 *  is `{image_id, url}`, `url` the ORIGINAL on the media CDN — show it downsized through
 *  `imageChunksFromUrl` + `imageUrl(…, preset)`, never inline. */
import { catalogWriteErrors } from './catalogWriteErrors';

import { ConsoleApiError, client } from './client';
import type { components } from './types';

export type StagedImagePurpose = components['schemas']['ConsoleStagedImagePurpose'];
export type StagedImage = components['schemas']['ConsoleStagedImage'];
export type StagedImageRejectionCode = components['schemas']['ConsoleStagedImageRejection']['code'];
type CatalogRecordErrorCode = components['schemas']['CatalogRecordErrorCode'];

export interface StagedUploadOptions {
  signal?: AbortSignal;
  /** The share of the file sent so far, 0–1; null while the total is unknown. */
  onProgress?: (fraction: number | null) => void;
}

/** What ImageWidget's `uploaded` staging calls with a picked file: a record kind binds the
 *  purpose and the record's tenant (`stagedImageUploader`). */
export type StagedImageUpload = (file: File, opts?: StagedUploadOptions) => Promise<StagedImage>;

/** A 10 MB file on a slow link outlasts the client's 30 s default. */
const UPLOAD_TIMEOUT_MS = 120_000;

export async function uploadStagedImage(
  file: File,
  body: { purpose: StagedImagePurpose; tour_operator: number },
  opts: StagedUploadOptions = {},
): Promise<StagedImage> {
  const fd = new FormData();
  fd.append('_json', JSON.stringify(body));
  fd.append('file', file);
  const r = await client.post<StagedImage>('/media/staged/', fd, {
    headers: { 'Content-Type': undefined },
    timeout: UPLOAD_TIMEOUT_MS,
    signal: opts.signal,
    onUploadProgress: (e) => opts.onProgress?.(e.total ? Math.min(1, e.loaded / e.total) : null),
  });
  return r.data;
}

/** The uploader for one field of one record: its purpose and the record's tenant (the
 *  Image belongs to it; the save refuses another operator's image). */
export function stagedImageUploader(purpose: StagedImagePurpose, tourOperator: number): StagedImageUpload {
  return (file, opts) => uploadStagedImage(file, { purpose, tour_operator: tourOperator }, opts);
}

/** Each refusal's own words, for a body that carries none (a proxy's HTML page, a 413
 *  the server refused before reading the body). The server's `detail` always wins. */
const REJECTION_WORDS: Readonly<Record<StagedImageRejectionCode, string>> = {
  image_missing: 'No file was sent.',
  image_empty: 'The file is empty.',
  image_too_large: 'The image is too large: at most 10 MB and 50 megapixels.',
  image_type: 'This file is not an image the app can show: use JPG, PNG, GIF or WebP.',
  image_unreadable: 'The image could not be read.',
  svg_active_content: 'This SVG has scripts or links to other files.',
};

function rejectionWords(code: unknown): string | null {
  return typeof code === 'string' && code in REJECTION_WORDS ? REJECTION_WORDS[code as StagedImageRejectionCode] : null;
}

/** Why an upload failed, for the operator: the server's message (`detail`, else its copy
 *  `message`, else a field-keyed one — an unknown purpose or tenant), else the refusal
 *  code's words, else the status's. */
export function stagedImageErrorMessage(e: unknown): string {
  if (!(e instanceof ConsoleApiError)) return 'The upload failed. Check the connection and try again.';
  const raw: unknown = e.body;
  const body = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const split = catalogWriteErrors(e);
  if (split) {
    const message = split.form ?? Object.values(split.fields)[0];
    if (message) return message;
  } else {
    // A 413's body is the same rejection (`image_too_large`).
    for (const key of ['detail', 'message'] as const) {
      const v = body[key];
      if (typeof v === 'string' && v) return v;
    }
  }
  const words = rejectionWords(body.code);
  if (words) return words;
  if (e.status === 413) return REJECTION_WORDS.image_too_large;
  if (e.status === 403) return 'You may not upload images for this record.';
  return 'The upload failed. Try again.';
}

/** The refusal of an image id whose image no longer exists — most often an upload no save
 *  used within a day, which the daily sweep removed (`CatalogRecordErrorCode`). */
export const STAGED_IMAGE_GONE = 'staged_image_gone' satisfies CatalogRecordErrorCode;

/** A save (or preview) that names an upload the daily sweep removed: the field's refusal
 *  carries `staged_image_gone` (its words end "Upload it again."), so the editor asks for the
 *  file again. An image of another operator stays `invalid_value`. */
export function isSweptUpload(refusal: { code: string }): boolean {
  return refusal.code === STAGED_IMAGE_GONE;
}
